import type { Transform } from "redux-persist";

import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import localforage from "localforage";
import { combineReducers } from "redux";
import { createMigrate, createTransform, persistReducer, persistStore } from "redux-persist";

import { registerErrorLogSink } from "~/helpers/logger";
import resolveSessionIdentity, { type PersistedSessionIdentity } from "~/helpers/persistedSessionIdentity";
import { adminApi } from "~/services/adminApi";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { intelligenceApi } from "~/services/intelligenceApi";
import agGridReducer from "~/store/agGridSlice";
import allocationReducer from "~/store/allocationSlice";
import availabilityReducer, { type AvailabilityState } from "~/store/availabilitySlice";
import bulkInviteReducer from "~/store/bulkInviteSlice";
import consolidationReducer from "~/store/consolidationSlice";
import countLineReducer from "~/store/countLineSlice";
import countSessionsReducer from "~/store/countSessionsSlice";
import createOrderReducer from "~/store/createOrderSlice";
import customerReducer from "~/store/customerSlice";
import dockLayoutReducer, { DOCK_LAYOUT_SCHEMA_VERSION, type DockLayoutState } from "~/store/dockLayoutSlice";
import engageReducer, { type EngageState } from "~/store/engageSlice";
import entityContextReducer from "~/store/entityContextSlice";
import errorReducer from "~/store/errorSlice";
import eventScheduleReducer from "~/store/eventScheduleSlice";
import eventsDaySnapshotReducer, {
	type EventsDaySnapshotState,
	restoreEventsDaySnapshot
} from "~/store/eventsDaySnapshotSlice";
import eventsManagementReducer from "~/store/eventsManagementSlice";
import eventsNoteDraftReducer from "~/store/eventsNoteDraftSlice";
import eventsTimerReducer, { type EventsTimerState } from "~/store/eventsTimerSlice";
import featureFlagCacheReducer from "~/store/featureFlagCacheSlice";
import fernieActionReducer from "~/store/fernieActionSlice";
import forecastReducer from "~/store/forecastSlice";
import generatePoRequestReducer from "~/store/generatePoRequestSlice";
import gridRefreshReducer from "~/store/gridRefreshSlice";
import hardgoodsInventoryReducer from "~/store/hardgoodsInventorySlice";
import integrationWizardReducer from "~/store/integrationWizardSlice";
import intelligenceReducer from "~/store/intelligenceSlice";
import inventoryReducer from "~/store/inventorySlice";
import itemsManagementReducer from "~/store/itemsManagementSlice";
import kitsReducer from "~/store/kitsSlice";
import laborScheduleReducer from "~/store/laborScheduleSlice";
import laborTypeReducer from "~/store/laborTypeSlice";
import layoutReducer from "~/store/layoutSlice";
import loadListReducer from "~/store/loadListSlice";
import masterOrderReducer from "~/store/masterOrderSlice";
import materialMobileReducer, { type MaterialMobileState } from "~/store/materialMobileSlice";
import materialPendingWritesReducer from "~/store/materialPendingWritesSlice";
import materialSetupReducer from "~/store/materialSetupSlice";
import materialReducer from "~/store/materialSlice";
import crossTabConfirmationSyncMiddleware from "~/store/middleware/crossTabConfirmationSync";
import { errorLoggingMiddleware } from "~/store/middleware/errorLoggingMiddleware";
import siteTenantStampMiddleware from "~/store/middleware/siteTenantStampMiddleware";
import migrateLegacyAvailabilityActionQueue, {
	type LegacyAvailabilityState
} from "~/store/migrateLegacyAvailabilityActionQueue";
import migrateLegacyCachedHistory from "~/store/migrateLegacyCachedHistory";
import migrateLegacyEngagePostQueue, { type LegacyEngageState } from "~/store/migrateLegacyEngagePostQueue";
import migrateLegacyPendingShips from "~/store/migrateLegacyPendingShips";
import migrateLegacyShrinkOperationQueue, {
	type LegacyShrinkMobileState
} from "~/store/migrateLegacyShrinkOperationQueue";
import migrateLegacyVerifySubmitQueue, { type LegacyVerifyState } from "~/store/migrateLegacyVerifySubmitQueue";
import offlineQueueReducer from "~/store/offlineQueueSlice";
import orderReducer from "~/store/orderSlice";
import overheadRateReducer from "~/store/overheadRateSlice";
import pendingIntentReducer from "~/store/pendingIntentSlice";
import persistMigrations, { PERSIST_VERSION } from "~/store/persistMigrations";
import phoneVerificationReducer from "~/store/phoneVerificationSlice";
import portalRetailPriceDraftReducer from "~/store/portalRetailPriceDraftSlice";
import portalReducer, { type PortalState } from "~/store/portalSlice";
import priceReducer from "~/store/priceSlice";
import productionScheduleReducer from "~/store/productionScheduleSlice";
import productionTriggerReducer from "~/store/productionTriggerSlice";
import productReducer from "~/store/productSlice";
import purchasingScheduleReducer from "~/store/purchasingScheduleSlice";
import readCacheReducer from "~/store/readCacheSlice";
import reapplyRecipesReducer from "~/store/reapplyRecipesSlice";
import recipesReducer from "~/store/recipesSlice";
import rekeyVerifyContainersById from "~/store/rekeyVerifyContainersById";
import reportParametersReducer from "~/store/reportParametersSlice";
import reportRunsReducer from "~/store/reportRunsSlice";
import allocationEngineReducer from "~/store/restock/allocationEngineSlice";
import restockContainersReducer from "~/store/restock/containersSlice";
import deliveryScheduleReducer from "~/store/restock/deliveryScheduleSlice";
import restockItemsReducer from "~/store/restock/itemsSlice";
import restockKittingReducer from "~/store/restock/kittingSlice";
import restockLocationsReducer from "~/store/restock/locationsSlice";
import restockOrderReducer from "~/store/restock/restockOrderSlice";
import restockRulesReducer from "~/store/restock/rulesSlice";
import routeDeliveryScheduleReducer from "~/store/routeDeliveryScheduleSlice";
import settingsReducer from "~/store/settingsSlice";
import shrinkEntryReducer from "~/store/shrinkEntrySlice";
import shrinkMobileReducer, { type ShrinkMobileState } from "~/store/shrinkMobileSlice";
import siteLocationReducer from "~/store/siteLocationSlice";
import siteReducer from "~/store/siteSlice";
import spacePlanningReducer from "~/store/spacePlanningSlice";
import storageDurabilityReducer from "~/store/storageDurabilitySlice";
import storeClassReducer from "~/store/storeClassSlice";
import syncActivityReducer from "~/store/syncActivitySlice";
import syncModeReducer from "~/store/syncModeSlice";
import taskAdminReducer from "~/store/taskAdminSlice";
import tenantAccessReducer from "~/store/tenantAccessSlice";
import termsReducer from "~/store/termsSlice";
import trialLimitReducer from "~/store/trialLimitSlice";
import vendorOrderReducer from "~/store/vendorOrderSlice";
import vendorReducer from "~/store/vendorSlice";
import verifyReducer, { type VerifyState } from "~/store/verifySlice";
import walkthroughReducer from "~/store/walkthroughSlice";

const eventsTimerTransform = createTransform(
	(inboundState: EventsTimerState) => inboundState,
	(outboundState: EventsTimerState) => ({
		...outboundState,
		// Persist activeTimer through rehydration: it now mirrors a durable server-side
		// FloatingTimeEntry (StartFloatingTimer creates one), and a refresh while a timer
		// is running must not lose the floatingTimeEntryId — without it the user has no
		// UI path to Stop or Link, and wsapi's one-timer-per-worker rule blocks new starts.
		// Pre-cutover (PR 6109) FloatingEntry records on disk lack the allocations field.
		// Normalize to [] on rehydrate so linkEntryToTask can read entry.allocations.length safely.
		floatingEntries: (outboundState.floatingEntries ?? []).map((entry) => ({
			...entry,
			allocations: entry.allocations ?? []
		})),
		scannedJob: undefined
	}),
	{ whitelist: ["eventsTimer"] }
);

const portalTransform = createTransform(
	(inboundState: PortalState) => ({ ...inboundState, cartOpen: false }),
	(outboundState) => outboundState as PortalState,
	{ whitelist: ["portal"] }
);

export const engageTransform = createTransform(
	(inboundState: EngageState) => ({
		...inboundState,
		cameraScanMode: false,
		lastPostReplay: null,
		quickScanMode: false,
		restoredLocationId: undefined
	}),
	(outboundState: LegacyEngageState) => ({
		...migrateLegacyEngagePostQueue(outboundState),
		cameraScanMode: false,
		lastPostReplay: null,
		postActionKeys: outboundState.postActionKeys ?? {},
		quickScanMode: false,
		recentLocationIds: outboundState.recentLocationIds ?? [],
		restoredLocationId: outboundState.selectedLocationId
	}),
	{ whitelist: ["engage"] }
);

const EPHEMERAL_ASSIGNED_COUNT_IN_FLIGHT: MaterialMobileState["assignedCountInFlight"] = {};

const materialMobileTransform = createTransform(
	(inboundState: MaterialMobileState) => ({
		...inboundState,
		assignedCountInFlight: EPHEMERAL_ASSIGNED_COUNT_IN_FLIGHT,
		countConfirmationWatch: undefined,
		lastQueueSuccessMutation: undefined,
		queueSuccessSignal: 0
	}),
	(outboundState: MaterialMobileState) => ({
		...migrateLegacyPendingShips(migrateLegacyCachedHistory(outboundState)),
		assignedCountInFlight: EPHEMERAL_ASSIGNED_COUNT_IN_FLIGHT,
		cachedLocationQuantitiesByLocation: outboundState.cachedLocationQuantitiesByLocation ?? {},
		cachedLocationQuantitiesLastUpdate: outboundState.cachedLocationQuantitiesLastUpdate ?? {},
		countConfirmationWatch: undefined,
		lastQueueSuccessMutation: undefined,
		pendingActivityLog: outboundState.pendingActivityLog ?? {},
		queueSuccessSignal: 0
	}),
	{ whitelist: ["materialMobile"] }
);

const shrinkMobileTransform = createTransform(
	(inboundState: ShrinkMobileState) => ({ ...inboundState, lastReplayEvent: null }),
	(outboundState: LegacyShrinkMobileState) => migrateLegacyShrinkOperationQueue(outboundState),
	{ whitelist: ["shrinkMobile"] }
);

const patchesHeldForUnknownCounts = (state: AvailabilityState): AvailabilityState["pendingPatches"] => {
	const heldKeys = new Set(
		Object.values(state.unknownCountPosts ?? {}).flatMap((posts) => posts.map((post) => post.actionKey))
	);
	return Object.fromEntries(
		Object.entries(state.pendingPatches ?? {}).filter(([actionKey]) => heldKeys.has(actionKey))
	);
};

const EPHEMERAL_POSTS_IN_FLIGHT: AvailabilityState["postsInFlight"] = {};

export const availabilityTransform = createTransform(
	(inboundState: AvailabilityState) => ({
		...inboundState,
		pendingPatches: patchesHeldForUnknownCounts(inboundState),
		postsInFlight: EPHEMERAL_POSTS_IN_FLIGHT
	}),
	(outboundState: LegacyAvailabilityState) => {
		const migratedState = migrateLegacyAvailabilityActionQueue(outboundState);
		return {
			...migratedState,
			activityLog: migratedState.activityLog ?? [],
			cachedInventoryByLocation: migratedState.cachedInventoryByLocation ?? {},
			cachedInventoryLastUpdate: migratedState.cachedInventoryLastUpdate ?? {},
			draftCounts: migratedState.draftCounts ?? {},
			historyReasonFilter: migratedState.historyReasonFilter ?? [],
			pendingPatches: patchesHeldForUnknownCounts(migratedState),
			postActionKeys: migratedState.postActionKeys ?? {},
			postFailures: migratedState.postFailures ?? {},
			postsInFlight: EPHEMERAL_POSTS_IN_FLIGHT,
			unknownCountPosts: migratedState.unknownCountPosts ?? {},
			unpostedCounts: migratedState.unpostedCounts ?? {}
		};
	},
	{ whitelist: ["availability"] }
);

const verifyTransform = createTransform(
	(inboundState: VerifyState) => ({ ...inboundState, activeContainerId: null }),
	(outboundState: LegacyVerifyState) => rekeyVerifyContainersById(migrateLegacyVerifySubmitQueue(outboundState)),
	{ whitelist: ["verify"] }
);

const identityStampMatches = (stampedValue: string | undefined, liveValue: string | undefined): boolean =>
	Boolean(stampedValue) && Boolean(liveValue) && stampedValue === liveValue;

const sessionIdentityMatches = (stamped: PersistedSessionIdentity, identity: PersistedSessionIdentity): boolean =>
	identityStampMatches(stamped.tenantId, identity.tenantId) &&
	identityStampMatches(stamped.userEmail, identity.userEmail);

const dockLayoutMatchesIdentity = (dockLayout: DockLayoutState, identity: PersistedSessionIdentity): boolean =>
	dockLayout.dockLayoutSchemaVersion === DOCK_LAYOUT_SCHEMA_VERSION && sessionIdentityMatches(dockLayout, identity);

export const dockLayoutTransform = createTransform(
	(inboundState: DockLayoutState) => ({
		...inboundState,
		dockLayoutSchemaVersion: DOCK_LAYOUT_SCHEMA_VERSION,
		...resolveSessionIdentity()
	}),
	(outboundState: DockLayoutState) => {
		const identity = resolveSessionIdentity();
		return {
			...outboundState,
			...identity,
			dockLayoutSchemaVersion: DOCK_LAYOUT_SCHEMA_VERSION,
			layouts: dockLayoutMatchesIdentity(outboundState, identity) ? (outboundState.layouts ?? {}) : {}
		};
	},
	{ whitelist: ["dockLayout"] }
);

const EMPTY_EVENTS_DAY_SNAPSHOT: EventsDaySnapshotState = { jobs: {} };

export const eventsDaySnapshotTransform = createTransform(
	(inboundState: EventsDaySnapshotState) => ({ ...inboundState, ...resolveSessionIdentity() }),
	(outboundState: EventsDaySnapshotState) =>
		sessionIdentityMatches(outboundState, resolveSessionIdentity())
			? restoreEventsDaySnapshot(outboundState)
			: EMPTY_EVENTS_DAY_SNAPSHOT,
	{ whitelist: ["eventsDaySnapshot"] }
);

const reducer = combineReducers({
	[adminApi.reducerPath]: adminApi.reducer,
	aggrid: agGridReducer,
	allocation: allocationReducer,
	allocationEngine: allocationEngineReducer,
	[apiEndpoints.reducerPath]: apiEndpoints.reducer,
	availability: availabilityReducer,
	bulkInvite: bulkInviteReducer,
	consolidation: consolidationReducer,
	countLine: countLineReducer,
	countSessions: countSessionsReducer,
	createOrder: createOrderReducer,
	customer: customerReducer,
	deliverySchedule: deliveryScheduleReducer,
	dockLayout: dockLayoutReducer,
	engage: engageReducer,
	entityContext: entityContextReducer,
	error: errorReducer,
	eventSchedule: eventScheduleReducer,
	fernieAction: fernieActionReducer,
	forecast: forecastReducer,
	generatePoRequest: generatePoRequestReducer,
	gridRefresh: gridRefreshReducer,
	hardgoodsInventory: hardgoodsInventoryReducer,
	eventsDaySnapshot: eventsDaySnapshotReducer,
	eventsManagement: eventsManagementReducer,
	eventsNoteDraft: eventsNoteDraftReducer,
	eventsTimer: eventsTimerReducer,
	featureFlagCache: featureFlagCacheReducer,
	integrationWizard: integrationWizardReducer,
	intelligence: intelligenceReducer,
	// TODO: merge into apiEndpoints when intelligence schema lands on main API
	[intelligenceApi.reducerPath]: intelligenceApi.reducer,
	inventory: inventoryReducer,
	itemsManagement: itemsManagementReducer,
	kits: kitsReducer,
	laborSchedule: laborScheduleReducer,
	laborType: laborTypeReducer,
	layout: layoutReducer,
	loadList: loadListReducer,
	masterOrder: masterOrderReducer,
	material: materialReducer,
	materialMobile: materialMobileReducer,
	materialPendingWrites: materialPendingWritesReducer,
	materialSetup: materialSetupReducer,
	offlineQueue: offlineQueueReducer,
	order: orderReducer,
	overheadRate: overheadRateReducer,
	pendingIntent: pendingIntentReducer,
	phoneVerification: phoneVerificationReducer,
	portal: portalReducer,
	portalRetailPriceDraft: portalRetailPriceDraftReducer,
	price: priceReducer,
	product: productReducer,
	productionSchedule: productionScheduleReducer,
	productionTrigger: productionTriggerReducer,
	purchasingSchedule: purchasingScheduleReducer,
	readCache: readCacheReducer,
	reapplyRecipes: reapplyRecipesReducer,
	recipes: recipesReducer,
	reportParameters: reportParametersReducer,
	reportRuns: reportRunsReducer,
	restockContainers: restockContainersReducer,
	restockItems: restockItemsReducer,
	restockKitting: restockKittingReducer,
	restockLocations: restockLocationsReducer,
	restockOrder: restockOrderReducer,
	restockRules: restockRulesReducer,
	routeDeliverySchedule: routeDeliveryScheduleReducer,
	settings: settingsReducer,
	shrinkEntry: shrinkEntryReducer,
	shrinkMobile: shrinkMobileReducer,
	site: siteReducer,
	siteLocation: siteLocationReducer,
	spacePlanning: spacePlanningReducer,
	storageDurability: storageDurabilityReducer,
	storeClass: storeClassReducer,
	syncActivity: syncActivityReducer,
	syncMode: syncModeReducer,
	taskAdmin: taskAdminReducer,
	tenantAccess: tenantAccessReducer,
	terms: termsReducer,
	trialLimit: trialLimitReducer,
	vendor: vendorReducer,
	vendorOrder: vendorOrderReducer,
	verify: verifyReducer,
	walkthrough: walkthroughReducer
});

type PersistedState = ReturnType<typeof reducer>;

export const persistConfig = {
	key: "redux",
	migrate: createMigrate(persistMigrations, { debug: false }),
	storage: localforage,
	// Cart-line changes flush immediately; other updates retain redux-persist's throttle.
	throttle: 150,
	version: PERSIST_VERSION,
	transforms: [
		eventsTimerTransform,
		portalTransform,
		engageTransform,
		materialMobileTransform,
		shrinkMobileTransform,
		availabilityTransform,
		verifyTransform,
		dockLayoutTransform,
		eventsDaySnapshotTransform
	] as unknown as Transform<PersistedState, PersistedState>[],
	// Non-persisted slices (API-fetched, transient): laborType, purchasingSchedule, vendor, restockOrder,
	// syncMode, syncActivity, storageDurability.
	// readCache is NOT whitelisted — a persisted entry age would outlive the cache entry it describes.
	// offlineQueue is deliberately NOT whitelisted — it mirrors the durable Workbox IDB queue;
	// persisting the mirror would drift from IDB on refresh. The SW replies to REQUEST_QUEUE_STATE on
	// mount so the slice reconciles from the authoritative source.
	whitelist: [
		"site",
		"error",
		"settings",
		"availability",
		"dockLayout",
		"engage",
		"eventsDaySnapshot",
		"eventsNoteDraft",
		"eventsTimer",
		"featureFlagCache",
		"forecast",
		"portal",
		"materialMobile",
		"shrinkMobile",
		"verify"
	]
};

// LOGOUT action type — when dispatched, all slices reset to initial state
export const LOGOUT = "LOGOUT";

const rootReducer: typeof reducer = (state, action) => {
	if (action.type === LOGOUT) {
		// Pass undefined to reset all slices to their initial state
		return reducer(undefined, action);
	}
	return reducer(state, action);
};

const persistedReducer = persistReducer<PersistedState>(persistConfig, rootReducer) as unknown as typeof rootReducer;
export const store = configureStore({
	devTools: process.env.NODE_ENV === "development",
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			immutableCheck: false, // Disable for dev performance reasons
			serializableCheck: false
			/*serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},*/
		})
			.concat(apiEndpoints.middleware)
			// TODO: merge into apiEndpoints when intelligence schema lands on main API
			.concat(intelligenceApi.middleware)
			.concat(adminApi.middleware)
			.concat(errorLoggingMiddleware)
			.concat(crossTabConfirmationSyncMiddleware)
			.concat(siteTenantStampMiddleware),
	reducer: persistedReducer
});
setupListeners(store.dispatch);
registerErrorLogSink(store.dispatch);
export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;
